<script setup>
import { computed, ref, onMounted } from "vue";
import { track } from "jiffy-analytics";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import ImagePreviewZoomed from "./ImagePreviewZoomed.vue";

const props = defineProps({
  lineItem: {
    type: Object,
    required: true
  },
  handleEdit: {
    type: Function,
    default: () => {}
  },
  shouldDisableEdit: {
    type: Boolean,
    required: true
  },
  previews: {
    type: Array,
    default: () => []
  }
});

const { lineItem, handleEdit, shouldDisableEdit } = props;
const modal = ref(null);
const activeImage = ref(0);

const close = () => {
  $(modal.value).modal("hide");
};

const open = () => {
  $(modal.value).modal("show");
  track("cart_preview_carousel_opened");
};

const switchImage = index => {
  activeImage.value = index;
  track("cart_preview_carousel_switch");
};

const title = computed(
  () =>
    `${lineItem.productName} - ${lineItem.brand?.name} ${lineItem.productStyle} - ${lineItem.productColor} ${lineItem.productSize}`
);
const previewUrls = computed(() => props.previews || []);
const isSingleImagePreview = computed(() => previewUrls.value.length <= 1);

onMounted(() => {
  $(modal.value).on("hidden.bs.modal", () => {
    track("cart_preview_carousel_closed");
  });
});

defineExpose({ open, close });
</script>

<template>
  <div
    ref="modal"
    class="image-preview-modal modal"
    role="dialog"
    data-testid="image-preview-modal"
  >
    <div class="image-preview-modal__modal-box">
      <div
        class="image-preview-modal__modal-header"
        role="heading"
        aria-label="Preview image products"
        tabIndex="0"
        data-testid="image-preview-modal-header"
      >
        <h3 class="image-preview-modal__modal-title" data-testid="image-preview-modal-title">
          {{ title }}
        </h3>

        <button
          aria-label="Close preview"
          class="image-preview-modal__modal-button-close"
          data-testid="image-preview-modal-close-btn"
          type="button"
          @click="close"
        >
          <UseSvg icon="modal-close" />
        </button>
      </div>

      <div
        v-if="lineItem.editUrl"
        class="image-preview-modal__edit-wrapper"
        data-testid="image-preview-modal-edit-wrapper"
      >
        <p class="image-preview-modal__edit-title" data-testid="image-preview-modal-edit-title">
          Want to make changes?
        </p>
        <a
          :aria-disabled="shouldDisableEdit ? 'true' : 'false'"
          class="image-preview-modal__edit"
          :class="{ 'image-preview-modal__edit--disabled': shouldDisableEdit }"
          data-testid="image-preview-modal-edit-design-link"
          :href="lineItem.editUrl"
          :tabindex="shouldDisableEdit ? -1 : 0"
          @click="handleEdit($event, true)"
        >
          <UseSvg icon="edit" class="image-preview-modal__edit-icon" /> Edit Design
        </a>
      </div>

      <div
        class="image-preview-modal__modal-body"
        :class="{ 'image-preview-modal__modal-body--single': isSingleImagePreview }"
        data-testid="image-preview-modal-body"
      >
        <ul
          v-if="!isSingleImagePreview"
          class="image-preview-modal__thumbnails-list"
          data-testid="image-preview-modal-thumbnails-list"
        >
          <li
            v-for="(url, index) in previewUrls"
            :key="url"
            class="image-preview-modal__thumbnails-list-item"
            :data-testid="`image-preview-modal-thumbnail-item-${index}`"
          >
            <a
              class="image-preview-modal__thumbnails-list-link"
              href="javascript:void()"
              :data-testid="`image-preview-modal-thumbnail-link-${index}`"
              @click.prevent="switchImage(index)"
            >
              <img
                :alt="`Thubnail ${index + 1}`"
                :src="url"
                class="image-preview-modal__thumbnails-list-image"
                :class="{
                  'image-preview-modal__thumbnails-list-image--active': activeImage === index
                }"
                :data-testid="`image-preview-modal-thumbnail-image-${index}`"
              />
            </a>
          </li>
        </ul>

        <div
          class="image-preview-modal__main-image-wrap"
          data-testid="image-preview-modal-main-image-wrap"
        >
          <ImagePreviewZoomed :alt="lineItem.productName" :src="previewUrls[activeImage]" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";
@import "frontend/init/jiffy/modal.scss";

.image-preview-modal {
  $this: &;

  @include crafterpay-modal;

  max-width: 900px;

  &__modal-box {
    top: 50%;
    right: auto;
    bottom: auto;
    left: 50%;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    max-width: none;
    border: none;
    border-radius: 0;
    transform: translate(-50%, -50%);

    @include with-tablet {
      height: auto;
      max-height: calc(100% - 80px);
      margin: 0;
      border-radius: 8px;
    }

    @include only-tablet() {
      width: calc(100% - 48px);
    }
  }

  &__modal-header {
    height: auto;
    min-height: unset;
    margin-bottom: 16px;
    padding: 16px;
    border: 0;
    border-bottom: 1px solid $crafterpay-gray-modern-200;
    box-shadow: 0 3px 6px 0px rgba($black, 0.1);

    @include with-tablet {
      padding: 12px 32px;
      box-shadow: none;
    }
  }

  &__modal-title {
    max-width: 90%;
    margin: 0;
    padding: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: $gray-modern-700;
    font-family: $digital-assets-font-family;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;

    @include with-tablet() {
      max-width: unset;
    }
  }

  &__modal-button-close {
    padding: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $enhance-grey-modern-400;

    & > svg {
      width: 20px;
      height: 20px;
    }

    &:hover {
      color: $fiord;
    }
  }

  &__modal-body {
    display: grid;
    align-content: start;
    gap: 16px;
    grid-template-columns: 1fr;
    height: auto;
    padding: 0 16px 8px;

    @include with-tablet {
      grid-template-columns: 1fr auto;
      padding: 0 24px 24px;

      &--single {
        grid-template-columns: 1fr;
      }
    }
  }

  &__thumbnails-list {
    display: flex;
    justify-content: flex-start;
    gap: 8px;
    margin-top: 0;
    overflow: auto;

    @include only-mobile {
      margin-top: 20px;
    }

    @include with-tablet() {
      display: grid;
      gap: 16px;
      grid-template-columns: 1fr;
      max-height: 100%;
      align-self: start;
    }

    &::-webkit-scrollbar {
      width: 10px;
      height: 2px;

      @include with-tablet() {
        width: 10px;
      }
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: $crafterpay-gray-modern-600;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: $crafterpay-gray-modern-700;
    }
  }

  &__thumbnails-list-item {
    flex-shrink: 0;
  }

  &__thumbnails-list-image {
    width: 80px;
    height: 80px;
    border: 1px solid $default-gray-200;
    border-radius: 8px;
    object-fit: contain;

    @include with-tablet() {
      width: 100px;
      height: 100px;
    }

    &--active {
      border-color: $blue-600;
      box-shadow: 0 0 0 1px $blue-600;
    }
  }

  &__main-image-wrap {
    display: flex;
    justify-content: center;
    order: -1;
    aspect-ratio: 1 / 1;
    position: relative;
    height: auto;
    min-height: unset;
    padding: 0;
    border: 1px solid $default-gray-200;
    border-radius: 8px;
    overflow: hidden;
    text-align: center;

    @include with-tablet {
      height: 100%;
      min-height: 370px;
      aspect-ratio: auto;
      padding: 24px;
    }
  }

  &__main-image {
    max-width: 100%;
    max-height: 100%;
    border: none;
  }

  &__nav {
    position: absolute;
    background: none;
    border: none;
    padding: 0;
    width: 20%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;

    &--prev {
      left: 0;
      align-items: flex-start;
    }

    &--next {
      right: 0;
      align-items: flex-end;
    }
  }

  &__nav-icon {
    color: $gray-body;
    width: 9px;
    height: 16px;

    @include with-tablet {
      width: 16px;
      height: 26px;
    }

    &--left {
      margin-left: -2px;
    }

    &--right {
      margin-right: -2px;
    }
  }

  &__nav-wrapper {
    border: 1px solid $gray-border;
    border-radius: 50%;
    background: rgba($white, 0.9);
    box-shadow: 0 2px 2px 0 rgba($gray-text, 0.1);
    height: 28px;
    width: 28px;
    margin: 0 5px;
    display: flex;
    align-items: center;
    justify-content: center;

    @include with-tablet {
      height: 40px;
      width: 40px;
      margin: 0 10px;
    }
  }

  &__edit {
    height: 40px;
    background-color: $white;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    padding-inline: 16px;
    gap: 5px;
    border-radius: 8px;
    border: 1px solid $default-gray-900;
    font-family: $digital-assets-font-family;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    color: $default-gray-900;

    & > svg {
      width: 24px;
      height: 24px;
    }

    &--disabled {
      pointer-events: none;
      opacity: 0.5;
    }

    &:hover,
    &:focus {
      color: $white;
      background-color: $default-gray-900;
      text-decoration: none;
    }
  }

  &__edit-wrapper {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 0 16px;
    margin-bottom: 16px;
    gap: 12px;

    @include with-tablet() {
      padding: 0 24px;
    }
  }

  &__edit-title {
    font-family: $digital-assets-font-family;
    font-size: 16px;
    font-weight: 500;
    line-height: 28px;
    color: $default-gray-900;
    margin-bottom: 0;

    @include with-tablet() {
      font-size: 18px;
    }
  }
}
</style>
